<template>
  <div class="card">
    <h2>ایجاد اتاق بازی</h2>
    <form class="form" @submit.prevent="onSubmit">
      <div class="field">
        <label>نام اتاق</label>
        <input v-model.trim="name" type="text" required />
      </div>
      <div class="field">
        <label>نام کودک</label>
        <input v-model.trim="childName" type="text" required />
      </div>
      <div class="field">
        <label>زمان پایان</label>
        <input v-model="endAt" type="datetime-local" required />
      </div>
      <div class="field">
        <label>سن کودک (اختیاری)</label>
        <input v-model.number="age" type="number" min="1" max="18" />
      </div>
      <div class="field">
        <label>توضیحات (اختیاری)</label>
        <textarea v-model.trim="notes"></textarea>
      </div>
      
      <div class="field">
        <label>امنیت اتاق</label>
        <div class="sec-options">
          <label><input type="radio" v-model="securityType" value="none"> آزاد</label>
          <label><input type="radio" v-model="securityType" value="pin"> پین کد</label>
          <label><input type="radio" v-model="securityType" value="pattern"> الگو</label>
        </div>
      </div>
      <div v-if="securityType !== 'none'" class="field">
        <label v-if="securityType === 'pin'">رمز 4 رقمی</label>
        <label v-else>الگو</label>
        <input v-if="securityType === 'pin'" v-model.trim="securityValue" type="text" placeholder="1234" />
        <PatternInput v-else v-model="securityValue" />
      </div>
      <button class="primary" :disabled="!canSubmit">ایجاد اتاق</button>
    </form>

    <div v-if="room" class="result">
      <h3>اتاق ایجاد شد</h3>
      <p>لینک اتاق: <a :href="roomLink" target="_blank">{{ roomLink }}</a></p>
      <canvas ref="qrCanvas"></canvas>
      <p class="muted">برای اشتراک، می‌توانید QR را ذخیره کنید.</p>
    </div>
  </div>
  <div class="lists">
    <div class="list">
      <h3>اتاق‌های فعال</h3>
      <div v-if="activeRooms.length === 0" class="muted">اتاق فعالی وجود ندارد</div>
      <div v-for="r in activeRooms" :key="r.slug" class="item">
        <div>
          <div><strong>{{ r.name }}</strong> — کودک: {{ r.childName }}</div>
          <div class="status">پایان: {{ r.endAt }}</div>
        </div>
        <div class="actions">
          <a :href="`${origin}/room/${r.slug}`" target="_blank" class="btn-link">ورود به اتاق</a>
          <a :href="`${origin}/dashboard/rooms/${r.slug}`" class="btn-link secondary">گالری & جزئیات</a>
        </div>
      </div>
    </div>
    <div class="list">
      <h3>اتاق‌های منقضی</h3>
      <div v-if="expiredRooms.length === 0" class="muted">اتاق منقضی وجود ندارد</div>
      <div v-for="r in expiredRooms" :key="r.slug" class="item">
        <div>
          <div><strong>{{ r.name }}</strong> — کودک: {{ r.childName }}</div>
          <div class="status">پایان: {{ r.endAt }}</div>
        </div>
        <div class="actions">
          <a :href="`${origin}/dashboard/rooms/${r.slug}`" class="btn-link secondary">مشاهده گالری</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { useQuery } from '@vue/apollo-composable'
import { CREATE_ROOM_MUTATION } from '../api/mutations'
import { ROOMS_MINE_QUERY, ROOMS_EXPIRED_QUERY } from '../api/queries'
import QRCode from 'qrcode'
import PatternInput from '../components/PatternInput.vue'

const name = ref('')
const childName = ref('')
const endAt = ref('')
const age = ref()
const notes = ref('')
const securityType = ref('none')
const securityValue = ref('')
const room = ref(null)
const qrCanvas = ref(null)

const canSubmit = computed(() => name.value && childName.value && endAt.value)

const { mutate: createRoom } = useMutation(CREATE_ROOM_MUTATION)
const { result: activeResult, refetch: refetchActive } = useQuery(ROOMS_MINE_QUERY)
const { result: expiredResult, refetch: refetchExpired } = useQuery(ROOMS_EXPIRED_QUERY, { limit: 10 })
const activeRooms = computed(() => activeResult.value?.roomsMine || [])
const expiredRooms = computed(() => expiredResult.value?.roomsExpiredMine || [])

async function onSubmit() {
  const isoEnd = endAt.value ? new Date(endAt.value).toISOString() : ''
  const { data } = await createRoom({ 
    data: { 
      name: name.value, 
      childName: childName.value, 
      endAt: isoEnd, 
      age: age.value || null, 
      notes: notes.value || null,
      securityType: securityType.value,
      securityValue: securityValue.value || null
    } 
  })
  room.value = data?.createRoom || null
  await nextTick()
  if (room.value && qrCanvas.value) {
    const link = roomLink.value
    await QRCode.toCanvas(qrCanvas.value, link, { width: 200 })
  }
  await refetchActive()
  await refetchExpired()
}

const origin = window.location.origin
const roomLink = computed(() => room.value ? `${origin}/room/${room.value.slug}` : '')
</script>

<script>
export default { name: 'RoomsView' }
</script>

<style scoped>
.card { background: var(--card); border:1px solid var(--border); border-radius:16px; padding:24px; box-shadow: 0 8px 24px rgba(0,0,0,0.06); }
.form { display:grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.field { display:flex; flex-direction:column; gap:6px; }
label { font-size:13px; color: var(--muted); }
input, textarea { border:1px solid var(--border); border-radius:10px; padding:10px 12px; font-size:14px; }
textarea { min-height: 80px; }
.primary { grid-column: 1 / -1; background: var(--accent); color:#000000; border:none; border-radius:10px; padding:10px 14px; font-weight:600; cursor:pointer; }
.result { margin-top:16px; border-top:1px solid var(--border); padding-top:16px; }
.lists { margin-top:20px; display:grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.list { border:1px solid var(--border); border-radius:12px; padding:12px; background:#fff; }
.list h3 { margin:0 0 8px; }
.item { display:flex; align-items:center; justify-content:space-between; padding:8px 0; border-bottom:1px dashed var(--border); }
.item:last-child { border-bottom:none; }
.status { font-size:12px; color: var(--muted); }
.muted { color: var(--muted); }

.actions {
  display: flex;
  gap: 8px;
  align-items: center;
}
.btn-link {
  text-decoration: none;
  color: var(--accent);
  font-size: 0.9rem;
  padding: 4px 8px;
  border-radius: 6px;
  background: rgba(0,0,0,0.05);
  transition: background 0.2s;
}
.btn-link:hover {
  background: rgba(0,0,0,0.1);
}
.btn-link.secondary {
  color: #e65100;
  background: #fff3e0;
}
.btn-link.secondary:hover {
  background: #ffe0b2;
}
.sec-options { display:flex; gap:12px; margin-top:4px; }
.sec-options label { display:flex; align-items:center; gap:4px; cursor:pointer; }
</style>
